import React, { useMemo, useCallback } from "react";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatToCustomLocale } from "../../../../utilities";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { selectBet, selectBets, selectCurrency, selectBetSelectPopupActive } from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { openBetSelectPopup, closeBetSelectPopup } from "../../../../State/actions";
import ButtonDecrementBet from "../Buttons/ButtonDecrementBet";
import ButtonIncrementBet from "../Buttons/ButtonIncrementBet";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

const BetDisplay = ({ toolbar, position }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const currency = useSelector(selectCurrency);
  const popupActive = useSelector(selectBetSelectPopupActive);
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();

  // Get button dimensions using the same system as PopiBalanceDisplay
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Get unified bet configuration (font multiplier, y position)
  // X position now comes from toolbar.layout
  const getBetConfig = () => {
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.65;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.25;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, y };
  };

  const betConfig = getBetConfig();
  const baseFontSize = 32;
  const responsiveFontSize = baseFontSize * betConfig.fontMultiplier;

  const FONT_SIZE = Math.round(responsiveFontSize * uiSymbolScale);
  const LABEL_FONT_SIZE = Math.round(FONT_SIZE * 0.9); // Slightly smaller for label

  const betLabelStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: LABEL_FONT_SIZE,
    fontWeight: "500",
    fill: 0xffffff,
    align: "left",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  });

  const formattedBet = formatToCustomLocale(bet);
  const betLabelText = t("bet");
  const betAmountText = `${formattedBet} ${currency}`;

  // Calculate max bet width based on largest bet in bets array (keeps buttons fixed)
  const maxBetWidth = useMemo(() => {
    if (!bets || bets.length === 0) {
      // Fallback: measure current bet if bets array not available
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
      });
      return TextMetrics.measureText(betAmountText, tempStyle).width;
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: FONT_SIZE,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [bets, currency, FONT_SIZE, betAmountText]);

  // Auto-scale font size if actual bet text exceeds max width (matches BalanceDisplay)
  const finalBetFontSize = useMemo(() => {
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: FONT_SIZE,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(betAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBetWidth) {
      const scale = maxBetWidth / metrics.width;
      return Math.round(FONT_SIZE * scale);
    }

    // Return ideal font size if it fits
    return FONT_SIZE;
  }, [betAmountText, FONT_SIZE, maxBetWidth]);

  const betAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalBetFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [finalBetFontSize]
  );

  // Measure text dimensions for positioning
  const labelMetrics = TextMetrics.measureText(betLabelText, betLabelStyle);
  const amountMetrics = TextMetrics.measureText(betAmountText, betAmountStyle);

  const totalHeight = labelMetrics.height + amountMetrics.height;
  const maxWidth = Math.max(labelMetrics.width, maxBetWidth);

  const labelY = Math.round(-totalHeight / 2 + labelMetrics.height / 2);
  const amountY = Math.round(totalHeight / 2 - amountMetrics.height / 2);

  // Button positioning
  const gap = buttonConfig.width * 0.075;

  // Calculate spacing: button + gap + text + gap + button
  const totalControlsWidth =
    buttonConfig.width + gap + maxWidth + gap + buttonConfig.width;

  // Position everything symmetrically around center
  const decrementX = -totalControlsWidth / 2;
  const textCenterX = Math.round(
    -totalControlsWidth / 2 + buttonConfig.width + gap + maxWidth / 2
  );
  const incrementX = totalControlsWidth / 2 - buttonConfig.width;

  // Use layout position from toolbar hook if available, with fallback to old logic
  const fallbackX = device === PixiSettings.DEVICE.MOBILE && orientation === PixiSettings.ORIENTATION.PORTRAIT
    ? toolbar.width - buttonConfig.width * 1.5
    : device === PixiSettings.DEVICE.TABLET && orientation === PixiSettings.ORIENTATION.PORTRAIT
    ? toolbar.width - buttonConfig.width * 2
    : buttonConfig.width * 8.5;

  const containerX = position?.x ?? toolbar.layout?.bet?.x ?? fallbackX;
  const containerY = position?.y ?? betConfig.y;
  const buttonsY = 0;
  const showButtons = device === PixiSettings.DEVICE.DESKTOP;
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;

  // Handle bet display click (desktop only - toggles bet select popup)
  const handleBetDisplayClick = useCallback(() => {
    if (isDesktop) {
      soundManager.play(SOUND_NAME.button_click);
      if (popupActive) {
        dispatch(closeBetSelectPopup());
      } else {
        dispatch(openBetSelectPopup());
      }
    }
  }, [isDesktop, popupActive, dispatch]);

  return (
    <Container name="bet_display" x={containerX} y={containerY} zIndex={5}>
      {/* Clickable text container (desktop only) */}
      <Container
        name="bet_text_container"
        eventMode={isDesktop ? "static" : "none"}
        cursor={isDesktop ? "pointer" : "default"}
        onpointertap={handleBetDisplayClick}
      >
        <Text
          text={betLabelText}
          style={betLabelStyle}
          anchor={{ x: 0.5, y: 0.5 }}
          x={textCenterX}
          y={labelY}
          resolution={2}
        />
        <Text
          text={betAmountText}
          style={betAmountStyle}
          anchor={{ x: 0.5, y: 0.5 }}
          x={textCenterX}
          y={amountY}
          resolution={2}
        />
      </Container>

      {showButtons && (
        <ButtonDecrementBet toolbar={toolbar} x={decrementX} y={buttonsY} />
      )}
      {showButtons && (
        <ButtonIncrementBet toolbar={toolbar} x={incrementX} y={buttonsY} />
      )}
    </Container>
  );
};

export default BetDisplay;
